<!--
 * Path: packages/frontend/src/features/browser/forms/link/Link.vue
 * Task: Wrapper for link fields utilizing FormBlockWrapper.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name"
    :info="field.info"
    :can-edit="canEdit"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="handleShowInfo"
  >
    <template #read>
      <LinkRead
        :field="field"
        :hide-header="true"
        @show-info="handleShowInfo"
      />
    </template>
    <template #edit>
      <LinkEdit
        v-model:url="urlProxy"
        v-model:name="nameProxy"
        @click:info="handleShowInfo"
      />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import LinkRead from './LinkRead.vue';
import LinkEdit from './LinkEdit.vue';

interface FieldDef {
  id: string;
  name: string;
  info?: string;
}

const { field } = defineProps<{ field: FieldDef }>();

const emit = defineEmits<{
  (e: 'show-info', payload: { title: string; text?: string }): void;
}>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value);
const activeEntity = computed(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

const handleShowInfo = (): void => {
  emit('show-info', { title: field.name, text: field.info });
};

const ensureDataObject = (): void => {
  if (activeEntity.value && !(activeEntity.value as any)[field.id]) {
    (activeEntity.value as any)[field.id] = { url: '', name: '' };
  }
};

const urlProxy = computed<string>({
  get: () => {
    return activeEntity.value ? ((activeEntity.value as any)[field.id]?.url || '') : '';
  },
  set: (val: string) => {
    ensureDataObject();
    if (activeEntity.value && (activeEntity.value as any)[field.id]) {
      (activeEntity.value as any)[field.id].url = val;
    }
  }
});

const nameProxy = computed<string>({
  get: () => {
    return activeEntity.value ? ((activeEntity.value as any)[field.id]?.name || '') : '';
  },
  set: (val: string) => {
    ensureDataObject();
    if (activeEntity.value && (activeEntity.value as any)[field.id]) {
      (activeEntity.value as any)[field.id].name = val;
    }
  }
});
</script>
